import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { City, Professional, Team } from "@syadem/ariane-js";
import {
  CertificationMethodEnum,
  CreateVaccinationActBodyVaccinationActVaccinator,
  InjectionLocationEnum,
  InjectionMethodEnum,
  VaccinationAct as ProVaccinationAct,
  ResponseError,
  VaccinationActCertificatorTypeEnum,
} from "@syadem/kairos-pro-js";
import { VaccinationAct as TeamVaccinationAct } from "@syadem/kairos-team-js";
import Polyglot from "node-polyglot";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Certificate, DomainVaccinationAct, getCertificate } from "../../domain/VaccinationAct";
import { Country } from "../../domain/country";
import { DomainHealthRecord } from "../../domain/healthRecord";
import { useAuthenticatedUser } from "../../store";
import { certificationDescription } from "../../utils/certificationDescription";
import { dateErrorTranslation } from "../../utils/date";
import { dayjs, localizedPlaceholder } from "../../utils/dayjs";
import { createTranslateFn, TranslateFunction } from "../../utils/formUtils";
import { isEditableVaccination } from "../../utils/isEditableVaccination";
import { compareNormalizedString } from "../../utils/string";
import { isVaccinationBoosterable } from "../../utils/vaccinationActs";
import { useDaphne } from "../hooks";
import { useAppContext } from "../hooks/useAppContext";
import { useArianeApi } from "../hooks/useArianeApi";
import { useCountryConfig } from "../hooks/useCountryConfig";
import { useI18n } from "../hooks/useI18n";
import { useServiceBus } from "../hooks/useServiceBus";
import { useVaccinesWithDiseases } from "../hooks/useVaccinesWithDiseases";
import { theme } from "../layout/Theme";
import AddSameVaccineDialog from "./AddSameVaccineDialog";
import AutoCloseAlert from "./AutoCloseAlert";
import { BoosterForm } from "./BoosterForm";
import DatamatrixButton from "./Datamatrix";
import FormVaccinesAutocomplete from "./FormVaccinesAutocomplete";
import { StyledAccordion } from "./mui/StyledAccordion";
import StyledAutoComplete from "./mui/StyledAutoComplete";
import { CancelButton, CancelLinkButton } from "./mui/StyledButtons";
import StyledDatepicker from "./mui/StyledDatepicker";
import { StyledInput } from "./mui/StyledInput";
import { StyledRadioGroup } from "./mui/StyledRadioGroup";
import { StyledSelect } from "./mui/StyledSelect";
import { ListItemDeleteButton } from "./shared/ListItemDeleteButton";
import { CitySearchField } from "./shared/CitySearchField";
import { ProfessionalSearchField } from "./shared/ProfessionalSearchField";

interface VaccinationActFormFieldValues {
  performedOn: Date | null;
  vaccineId: string;
  batchNumber: string;
  serialNumber: string;
  injectionLocation: InjectionLocationEnum | "";
  injectionMethod: InjectionMethodEnum | "";
  certificationMethod: CertificationMethodEnum;
  expirationDate?: Date;
  booster: boolean;
  countryCode: string;
  vaccinator: CreateVaccinationActBodyVaccinationActVaccinator | null;
}

function getVaccinator(
  firstName: string,
  lastName: string,
  externalId: string | null,
): CreateVaccinationActBodyVaccinationActVaccinator | null {
  if (firstName.length === 0 || lastName.length === 0) return null;

  return { firstName, lastName, externalId };
}

export function VaccinationActForm({
  isEmbedded = false,
  setSnackOpen,
  handleClose,
  isDatamatrixOpen,
  healthRecord,
  vaccinationAct,
}: {
  healthRecord: DomainHealthRecord;
  isEmbedded?: boolean;
  setSnackOpen?: (value: React.SetStateAction<boolean>) => void;
  handleClose?: () => void;
  isDatamatrixOpen?: boolean;
  vaccinationAct?: ProVaccinationAct | TeamVaccinationAct;
}) {
  const { t, locale, getObject } = useI18n();
  const daphne = useDaphne();
  const translateErrors: TranslateFunction = useMemo(() => createTranslateFn(t), [t]);
  const { isLoading: isLoadingVaccinesWithDiseases } = useVaccinesWithDiseases();
  const [isReadonly, setIsReadonly] = useState<boolean>(false);
  const [countryCodeInput, setCountryCodeInput] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | ReactNode | undefined>(undefined);
  const [certificators, setCertificators] = useState<(Professional | Team)[] | undefined>(undefined);
  const [openSameVaccinationDialog, setOpenSameVaccinationDialog] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const smallResolution = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const { defaultCountryCode } = useCountryConfig();
  const [performedOn, setPerformedOn] = useState<Date | null>(dayjs.utc(vaccinationAct?.performedOn).toDate());
  const [expirationDate, setExpirationDate] = useState(
    vaccinationAct?.expirationDate ? dayjs.utc(vaccinationAct?.expirationDate).toDate() : undefined,
  );
  const [vaccineId, setVaccineId] = useState(vaccinationAct?.vaccineId || "");
  const [countryCode, setCountryCode] = useState(vaccinationAct?.countryCode || defaultCountryCode);
  const [batchNumber, setBatchNumber] = useState(vaccinationAct?.batchNumber || "");
  const [serialNumber, setSerialNumber] = useState(vaccinationAct?.serialNumber || "");
  const [vaccinatorFirstName, setVaccinatorFirstName] = useState(vaccinationAct?.vaccinator?.firstName || "");
  const [vaccinatorLastName, setVaccinatorLastName] = useState(vaccinationAct?.vaccinator?.lastName || "");
  const [vaccinatorExternalId, setVaccinatorExternalId] = useState(vaccinationAct?.vaccinator?.externalId ?? null);
  const [vaccinatorNamesError, setVaccinatorNamesError] = useState(false);
  const [vaccinatorDialogOpen, setVaccinatorDialogOpen] = useState(false);
  const [vaccinator, setVaccinator] = useState<Professional | undefined>();
  const [vaccinatorCity, setVaccinatorCity] = useState<City | undefined>();
  const [injectionLocation, setInjectionLocation] = useState<InjectionLocationEnum | "">(
    vaccinationAct?.injectionLocation || "",
  );
  const [injectionMethod, setInjectionMethod] = useState<InjectionMethodEnum | "">(
    vaccinationAct?.injectionMethod || "",
  );
  const [certificationMethod, setCertificationMethod] = useState<CertificationMethodEnum>(
    vaccinationAct?.certificationMethod === null
      ? CertificationMethodEnum.NotValidated
      : vaccinationAct?.certificationMethod || CertificationMethodEnum.Execution,
  );
  const [booster, setBooster] = useState(vaccinationAct?.booster ?? false);
  const [isBoosterVisible, setIsBoosterVisible] = useState(
    booster || isVaccinationBoosterable(vaccineId, healthRecord.birthDate, performedOn, daphne),
  );

  const performedErrorMessage = useMemo(() => {
    const max = dayjs().endOf("day").toDate();
    return dateErrorTranslation(t, performedOn, { min: healthRecord.birthDate, max });
  }, [t, performedOn, healthRecord.birthDate]);

  const expirationDateErrorMessage = useMemo(() => {
    return dateErrorTranslation(t, expirationDate ?? null);
  }, [t, expirationDate]);

  // Submission
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { organizationId, teamId } = useAppContext();

  const arianeApi = useArianeApi();
  const currentUser = useAuthenticatedUser();
  const serviceBus = useServiceBus();
  const useCurrentUserAsVaccinator = !isReadonly && certificationMethod === CertificationMethodEnum.Execution;
  const effectiveVaccinatorFirstName = useCurrentUserAsVaccinator ? (currentUser.firstName ?? "") : vaccinatorFirstName;
  const effectiveVaccinatorLastName = useCurrentUserAsVaccinator ? (currentUser.lastName ?? "") : vaccinatorLastName;
  const effectiveVaccinatorExternalId = useCurrentUserAsVaccinator ? (currentUser.rpps ?? null) : vaccinatorExternalId;

  useEffect(() => {
    if (vaccinationAct) {
      setIsReadonly(!isEditableVaccination(vaccinationAct, currentUser?.id, teamId ?? undefined));
    }
  }, [vaccinationAct, currentUser, teamId]);

  useEffect(() => {
    if (vaccinationAct) {
      const act = vaccinationAct as TeamVaccinationAct;

      if (act.certificator) {
        const certificatorIds = [act.certificator.id];

        if (act.certificator.teamMemberAccountId) {
          certificatorIds.push(act.certificator.teamMemberAccountId);
        }

        arianeApi.professionals
          .searchByIds([...new Set(certificatorIds)])
          .then((result) => {
            setCertificators(result);
          })
          .catch((error) => {
            console.error(error);
            setCertificators([]);
          });
      }
    }
  }, [arianeApi.professionals, vaccinationAct]);

  const countries = useMemo(() => {
    return Object.entries(getObject("countries")).reduce((acc: Country[], [key, value]) => {
      acc.push({ code: key, name: value });
      return acc;
    }, []);
  }, [getObject]);

  let certificate: Certificate | null = null;

  if (vaccinationAct) {
    certificate = getCertificate(vaccinationAct as DomainVaccinationAct);
  }

  const [showAdditionalInfos, setShowAdditionalInfos] = useState(true);

  const handleDelete = useCallback(
    async (healthRecordId: string, id: string) => {
      serviceBus.dispatch({
        type: "deleteVaccinationAct",
        organizationId,
        teamId,
        healthRecordId,
        vaccinationActId: id,
      });
    },
    [serviceBus, organizationId, teamId],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (performedOn === null) return;

      const hasVaccinatorFirstName = effectiveVaccinatorFirstName.length > 0;
      const hasVaccinatorLastName = effectiveVaccinatorLastName.length > 0;

      if (hasVaccinatorFirstName !== hasVaccinatorLastName) {
        setVaccinatorNamesError(true);
        return;
      }

      setIsSubmitting(true);
      setErrorMessage(undefined);

      const values = {
        performedOn,
        vaccineId,
        countryCode,
        batchNumber,
        serialNumber,
        injectionLocation: injectionLocation as InjectionLocationEnum,
        injectionMethod: injectionMethod as InjectionMethodEnum,
        certificationMethod,
        booster,
        expirationDate,
        vaccinator: getVaccinator(
          effectiveVaccinatorFirstName,
          effectiveVaccinatorLastName,
          effectiveVaccinatorExternalId,
        ),
      };

      try {
        if (vaccinationAct) {
          const result = await serviceBus.dispatch({
            type: "updateVaccinationAct",
            organizationId,
            teamId,
            healthRecordId: healthRecord.id,
            vaccinationAct,
            updatedVaccinationAct: values,
          });

          if (result.type === "error") return;

          navigate(`..?updated=true`);
          if (setSnackOpen) setSnackOpen(true);
        } else {
          const vaccinationActs = healthRecord?.vaccinationActs || [];
          const existingVaccinationAct = vaccinationActs.find((vaccinationAct) => {
            return (
              dayjs(vaccinationAct.performedOn).isSame(values.performedOn, "day") &&
              vaccinationAct.vaccineId === values.vaccineId
            );
          });

          if (existingVaccinationAct) {
            setOpenSameVaccinationDialog(true);
          } else {
            const result = await serviceBus.dispatch({
              type: "createVaccinationAct",
              organizationId,
              teamId,
              healthRecordId: healthRecord.id,
              vaccinationAct: values,
            });

            if (result.type === "error") return;

            if (isEmbedded) {
              setBatchNumber("");
              setSerialNumber("");
              setInjectionMethod("");
              setInjectionLocation("");
              setExpirationDate(undefined);
              setAlertIsOpen(true);
            } else {
              navigate(`..?updated=true`);
            }
          }
        }
      } catch (e) {
        if ((e instanceof Response && e.status === 400) || (e instanceof ResponseError && e.response.status === 400)) {
          const response = e instanceof Response ? e : e.response;
          response.json().then((result) => {
            const code = result.error.code;

            switch (code) {
              case "act_cannot_be_performed_in_the_future_or_before_patient_birth_date":
                setErrorMessage(t("vaccines.error_messages.error_1"));
                break;
              case "act_cannot_be_performed_after_vaccine_expiration_date":
                setErrorMessage(t("vaccines.error_messages.error_2"));
                break;
              case "incorrect_injection_method":
                setErrorMessage(t("vaccines.error_messages.error_3"));
                break;
              case "incorrect_injection_location":
                setErrorMessage(t("vaccines.error_messages.error_4"));
                break;
            }
          });
        } else {
          setErrorMessage(t("common.alerts.alert_notification"));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      performedOn,
      vaccineId,
      countryCode,
      batchNumber,
      serialNumber,
      injectionLocation,
      injectionMethod,
      certificationMethod,
      booster,
      expirationDate,
      effectiveVaccinatorFirstName,
      effectiveVaccinatorLastName,
      effectiveVaccinatorExternalId,
      serviceBus,
      organizationId,
      teamId,
      navigate,
      setSnackOpen,
      isEmbedded,
      healthRecord,
      vaccinationAct,
      t,
    ],
  );

  const handleDatamatrixScan = (scanData: {
    vaccineId: string;
    expirationDate?: Date;
    batchNumber: string;
    serialNumber: string;
  }) => {
    setVaccineId(scanData.vaccineId);
    setExpirationDate(scanData.expirationDate);
    setBatchNumber(scanData.batchNumber);
    setSerialNumber(scanData.serialNumber);
    setCertificationMethod(CertificationMethodEnum.Execution);
    setVaccinatorNamesError(false);
  };

  const handleVaccineChange = (newVaccineId: string) => {
    setVaccineId(newVaccineId);
    const vaccinationIsBoosterable = isVaccinationBoosterable(
      newVaccineId,
      healthRecord.birthDate,
      performedOn,
      daphne,
    );
    if (vaccinationIsBoosterable) {
      setIsBoosterVisible(true);
    } else {
      setIsBoosterVisible(false);
      setBooster(false);
    }
  };

  return isLoadingVaccinesWithDiseases ? (
    <LinearProgress variant="query" />
  ) : (
    <>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <Input
          type="text"
          id="PreventChromeAutocomplete"
          name="PreventChromeAutocomplete"
          autoComplete="address-level4"
          sx={{ display: "none" }}
        />
        <Grid container rowSpacing={2} columnSpacing={3} justifyContent="space-between">
          <Grid size={12} container justifyContent="space-between">
            <Grid>
              <Typography variant="h6" marginBottom={1}>
                {vaccinationAct
                  ? isReadonly
                    ? t("vaccines.view_vaccination")
                    : t("vaccines.edit_vaccination")
                  : t("vaccines.add_vaccination")}
              </Typography>
            </Grid>
            {!isReadonly ? (
              <Grid>
                <DatamatrixButton onScanSuccess={handleDatamatrixScan} isOpen={isDatamatrixOpen} />
              </Grid>
            ) : null}
          </Grid>
          {errorMessage ? (
            <Grid size={12}>
              <Alert severity="error">{errorMessage}</Alert>
            </Grid>
          ) : null}
          <Grid container size={12} columnSpacing={2} rowSpacing={2}>
            <Grid size={12}>
              {isReadonly ? (
                vaccinationAct && certificate && certificators ? (
                  <>
                    <Typography variant="body2">
                      {certificationDescription(certificators, certificate, teamId ?? undefined, t, locale)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "grey" }}>
                      {certificationAdditionalInfo(vaccinationAct, t)}
                    </Typography>
                  </>
                ) : null
              ) : (
                <StyledRadioGroup
                  label="Validation"
                  fullWidth
                  value={certificationMethod}
                  onChange={({ target: { value } }) => {
                    setShowAdditionalInfos(value === CertificationMethodEnum.Execution);
                    setCertificationMethod(value as CertificationMethodEnum);
                    if (value === CertificationMethodEnum.Execution) setVaccinatorNamesError(false);
                  }}
                  groupName="certificationMethod"
                >
                  {certificate && certificators ? (
                    <Typography variant="body2" sx={{ marginBottom: "10px" }}>
                      {certificationDescription(certificators, certificate, teamId ?? undefined, t, locale)}
                    </Typography>
                  ) : null}
                  <FormControlLabel
                    value="execution"
                    control={<Radio size="small" data-testid="doValidateRadio" />}
                    label={<Typography variant="body2">{t("vaccines.confirmation.do_validate")}</Typography>}
                  />
                  <FormControlLabel
                    value="proof"
                    control={<Radio size="small" data-testid="validateRadio" />}
                    label={<Typography variant="body2">{t("vaccines.confirmation.validate")}</Typography>}
                  />
                  {vaccinationAct ? (
                    <FormControlLabel
                      value="not_validated"
                      control={<Radio size="small" data-testid="notValidatedRadio" />}
                      label={<Typography variant="body2">{t("vaccines.confirmation.not_validated")}</Typography>}
                    />
                  ) : null}
                </StyledRadioGroup>
              )}
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <StyledDatepicker
                required
                fullWidth
                acceptInvalidValue
                readOnly={isReadonly}
                name="performedOn"
                value={performedOn}
                placeholder={localizedPlaceholder(locale, t)}
                label={t("vaccines.date_act")}
                minDate={healthRecord.birthDate}
                maxDate={new Date()}
                onChange={(date) => {
                  setPerformedOn(date ?? null);
                }}
                error={!!performedErrorMessage}
                errorMessage={performedErrorMessage}
                warningText={
                  performedOn &&
                  expirationDate &&
                  dayjs(performedOn).isValid() &&
                  dayjs(expirationDate).isValid() &&
                  dayjs(performedOn).isAfter(dayjs(expirationDate))
                    ? t("vaccines.act_date_greater_than_expiration_date")
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 9 }} sx={{ display: "inline-flex", alignItems: "center" }}>
              <FormVaccinesAutocomplete
                vaccineId={vaccineId}
                onVaccineChange={handleVaccineChange}
                readOnly={isReadonly}
                performedOn={performedOn}
              />
              {isBoosterVisible ? (
                <BoosterForm
                  isReadonly={isReadonly}
                  booster={booster}
                  onBoosterChange={setBooster}
                  sx={{ mt: 2.5, ml: 2 }}
                />
              ) : null}
            </Grid>
          </Grid>
          <Grid size={12}>
            <StyledAccordion
              expanded={showAdditionalInfos}
              onChange={() => setShowAdditionalInfos((prevState) => !prevState)}
            >
              <AccordionSummary id="panel-header" aria-controls="panel-content" expandIcon={<ExpandMoreIcon />}>
                <Typography
                  sx={{
                    color: theme.palette.neutral[600],
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: ["Open Sans", "sans-serif"].join(","),
                  }}
                >
                  {t("vaccines.additional_information")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                  <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledInput
                        id="batchInput"
                        type="text"
                        name="batchNumber"
                        label={t("vaccines.batch_number")}
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder="ex : 3455673452372737"
                        autoComplete="cc-csc" // hack to not have credit card number validation message from Chrome because the label contains the word "Number"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledInput
                        id="serialInput"
                        name="serialNumber"
                        value={serialNumber}
                        label={t("vaccines.serial_number")}
                        onChange={(e) => setSerialNumber(e.target.value)}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder="ex : 27482783002389"
                        autoComplete="cc-csc" // hack to not have credit card number validation message from Chrome because the label contains the word "Number"
                      />
                    </Grid>
                  </Grid>
                  <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledDatepicker
                        fullWidth
                        readOnly={isReadonly}
                        name="expirationDate"
                        value={expirationDate ?? null}
                        placeholder={localizedPlaceholder(locale, t)}
                        label={t("vaccines.expiration_date")}
                        onChange={(date) => {
                          setExpirationDate(date);
                        }}
                        error={!!expirationDateErrorMessage}
                        errorMessage={expirationDateErrorMessage}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledAutoComplete
                        id="countryCode"
                        label={t("common.user_infos.country")}
                        translateErrors={translateErrors}
                        fullWidth
                        placeholder="ex : France"
                        required
                        readOnly={isReadonly}
                        options={countries}
                        filterOptions={(x) => x.filter((e) => compareNormalizedString(e.name, countryCodeInput || ""))}
                        getOptionLabel={(country: Country) => country.name || ""}
                        value={
                          countries.find((country) => country.code == (countryCode ?? vaccinationAct?.countryCode)) ||
                          null
                        }
                        onInputChange={(_event, newCountryCode, reason) => {
                          if (reason === "input") {
                            setCountryCodeInput(newCountryCode);
                          }
                        }}
                        onChange={(_event, newCountryCode) => {
                          if (!newCountryCode) {
                            setCountryCodeInput(undefined);
                            setCountryCode("");
                          } else {
                            setCountryCode(newCountryCode.code);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledSelect<InjectionMethodEnum | "">
                        name="injectionMethod"
                        value={injectionMethod}
                        onChange={(value) => setInjectionMethod(value.target.value)}
                        label={t("vaccines.injection_path")}
                        fullWidth
                        placeholder={`ex : ${t("injectionMethods.intramuscular")}`}
                        renderValue={(injectionMethod) => t(`injectionMethods.${injectionMethod}`)}
                        readOnly={isReadonly}
                      >
                        {Object.values(InjectionMethodEnum).map((filteredInjectionMethod) => (
                          <MenuItem key={filteredInjectionMethod} value={filteredInjectionMethod}>
                            {t(`injectionMethods.${filteredInjectionMethod}`)}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledSelect<InjectionLocationEnum | "">
                        name="injectionLocation"
                        value={injectionLocation}
                        onChange={(event) => setInjectionLocation(event.target.value)}
                        label={t("vaccines.injection_location")}
                        fullWidth
                        placeholder={`ex : ${t("injectionLocations.left_deltoid")}`}
                        renderValue={(injectionLocation) => t(`injectionLocations.${injectionLocation}`)}
                        readOnly={isReadonly}
                      >
                        {Object.values(InjectionLocationEnum).map((filteredInjectionLocation) => (
                          <MenuItem key={filteredInjectionLocation} value={filteredInjectionLocation}>
                            {t(`injectionLocations.${filteredInjectionLocation}`)}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </Grid>
                  </Grid>
                  <Grid size={12} container rowSpacing={0} mt={isReadonly || useCurrentUserAsVaccinator ? undefined : -0.8}>
                    <Grid
                      size={12}
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      mb={isReadonly || useCurrentUserAsVaccinator ? 0.5 : undefined}
                    >
                      <InputLabel
                        shrink
                        sx={{
                          color: theme.palette.neutral[600],
                          fontSize: 14,
                          fontWeight: 600,
                          fontFamily: ["Open Sans", "sans-serif"].join(","),
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          transition: "color 0.2s ease-in-out",
                          transform: "none",
                          "&.Mui-focused": {
                            color: isReadonly ? theme.palette.neutral[600] : theme.palette.primary.main,
                          },
                        }}
                      >
                        {t("vaccinator.vaccinator")}
                      </InputLabel>
                      {!isReadonly && !useCurrentUserAsVaccinator ? (
                        <Button
                          size="small"
                          startIcon={<SearchIcon fontSize="small" />}
                          onClick={() => setVaccinatorDialogOpen(true)}
                        >
                          {smallResolution ? t("vaccinator.search") : t("vaccinator.searchInDirectory")}
                        </Button>
                      ) : null}
                    </Grid>
                    <Grid size={6}>
                      <StyledInput
                        name="vaccinatorFirstName"
                        value={effectiveVaccinatorFirstName}
                        onChange={(event) => {
                          const firstName = event.target.value;
                          setVaccinatorFirstName(firstName);
                          setVaccinator(undefined);
                          setVaccinatorExternalId(null);
                          if (firstName.length > 0 === vaccinatorLastName.length > 0) {
                            setVaccinatorNamesError(false);
                          }
                        }}
                        error={vaccinatorNamesError && effectiveVaccinatorFirstName.length === 0}
                        readOnly={isReadonly || useCurrentUserAsVaccinator}
                        fullWidth
                        placeholder={!isReadonly ? t("vaccinator.firstName") : ""}
                      />
                    </Grid>
                    <Grid size={6}>
                      <StyledInput
                        name="vaccinatorLastName"
                        value={effectiveVaccinatorLastName}
                        onChange={(event) => {
                          const lastName = event.target.value;
                          setVaccinatorLastName(lastName);
                          setVaccinator(undefined);
                          setVaccinatorExternalId(null);
                          if (vaccinatorFirstName.length > 0 === lastName.length > 0) {
                            setVaccinatorNamesError(false);
                          }
                        }}
                        error={vaccinatorNamesError && effectiveVaccinatorLastName.length === 0}
                        readOnly={isReadonly || useCurrentUserAsVaccinator}
                        fullWidth
                        placeholder={!isReadonly ? t("vaccinator.lastName") : ""}
                      />
                    </Grid>
                    {vaccinatorNamesError ? (
                      <Grid size={12}>
                        <FormHelperText error>{t("vaccinator.namesRequired")}</FormHelperText>
                      </Grid>
                    ) : null}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </StyledAccordion>
          </Grid>
          <Grid size={12} container justifyContent="space-between" alignItems="center" marginTop="30px">
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              {isEmbedded ? (
                <CancelButton onClick={handleClose} sx={{ mr: 2 }}>
                  {t("common.cta.close")}
                </CancelButton>
              ) : null}
              {!isEmbedded ? (
                <CancelLinkButton
                  component={Link}
                  to={`${pathname.substring(0, pathname.lastIndexOf("/health-records") + 15)}/${healthRecord.id}`}
                  variant="contained"
                  disableElevation
                >
                  {t("common.cta.cancel")}
                </CancelLinkButton>
              ) : null}
              {!isReadonly && vaccinationAct ? (
                <ListItemDeleteButton
                  deleteRecord={() => handleDelete(vaccinationAct.healthRecordId, vaccinationAct.id)}
                  refreshCallback={() => {
                    navigate(
                      `${pathname.substring(0, pathname.lastIndexOf("/health-records") + 15)}/${healthRecord.id}`,
                    );
                  }}
                  title={t("common.cta.delete")}
                  modalTitle={t("vaccines.delete_vaccination")}
                  modalContent={t("vaccines.delete_vaccination_validation")}
                  icon={<DeleteOutlineIcon />}
                  sx={{ ml: 1 }}
                  variant="outlined"
                  buttonWidth="110px"
                />
              ) : null}
              <AutoCloseAlert
                icon={<CheckIcon fontSize="inherit" />}
                severity="success"
                open={alertIsOpen}
                handleClose={() => setAlertIsOpen(false)}
              >
                {t("vaccines.addVaccinationConfirmed")}
              </AutoCloseAlert>
            </Box>
            {!isReadonly ? (
              <Button variant="contained" disableElevation type="submit" loading={isSubmitting} data-testid="submit">
                {vaccinationAct ? t("common.cta.save") : t("common.cta.add")}
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </form>
      <AddSameVaccinationDialog
        healthRecordId={healthRecord.id}
        open={openSameVaccinationDialog}
        onClose={() => setOpenSameVaccinationDialog(false)}
        openAlert={() => setAlertIsOpen(true)}
        formValues={{
          performedOn,
          vaccineId,
          countryCode,
          batchNumber,
          serialNumber,
          injectionLocation,
          injectionMethod,
          certificationMethod,
          booster,
          expirationDate,
          vaccinator: getVaccinator(
            effectiveVaccinatorFirstName,
            effectiveVaccinatorLastName,
            effectiveVaccinatorExternalId,
          ),
        }}
      />
      <Dialog open={vaccinatorDialogOpen} onClose={() => setVaccinatorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("vaccinator.vaccinator")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography>{t("vaccinator.searchGuidance")}</Typography>
          <CitySearchField
            city={vaccinatorCity}
            onCityChange={(city) => {
              setVaccinatorCity(city);
              setVaccinator(undefined);
            }}
          />
          <ProfessionalSearchField
            city={vaccinatorCity}
            professional={vaccinator}
            professionalsOnly
            onProfessionalChange={setVaccinator}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Button onClick={() => setVaccinatorDialogOpen(false)} variant="outlined">
            {t("common.cta.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (vaccinator?.first_name && vaccinator.last_name) {
                setVaccinatorFirstName(vaccinator.first_name);
                setVaccinatorLastName(vaccinator.last_name);
                setVaccinatorExternalId(vaccinator.rpps ?? null);
                setVaccinatorNamesError(false);
                setVaccinatorDialogOpen(false);
              }
            }}
          >
            {t("common.cta.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function certificationAdditionalInfo(
  vaccinationAct: ProVaccinationAct | TeamVaccinationAct,
  t: (phrase: string, options?: number | Polyglot.InterpolationOptions | undefined) => string,
): string {
  if (vaccinationAct.certificator?.type == VaccinationActCertificatorTypeEnum.Professional) {
    return t("vaccines.editableByProOnly");
  } else if (vaccinationAct.certificator?.type == VaccinationActCertificatorTypeEnum.Team) {
    return t("vaccines.editableByTeamOnly");
  } else {
    return "";
  }
}

const AddSameVaccinationDialog = ({
  healthRecordId,
  open,
  onClose,
  openAlert,
  formValues,
}: {
  healthRecordId: string;
  open: boolean;
  onClose: () => void;
  openAlert: () => void;
  formValues: VaccinationActFormFieldValues;
}) => {
  const { organizationId, teamId } = useAppContext();
  const [isCreating, setIsCreating] = useState(false);
  const serviceBus = useServiceBus();

  const createVaccine = useCallback(async () => {
    if (formValues.performedOn === null) return;
    setIsCreating(true);

    const result = await serviceBus.dispatch({
      type: "createVaccinationAct",
      organizationId,
      teamId,
      healthRecordId,
      vaccinationAct: {
        vaccineId: formValues.vaccineId,
        countryCode: formValues.countryCode,
        performedOn: formValues.performedOn,
        batchNumber: formValues.batchNumber,
        serialNumber: formValues.serialNumber,
        injectionLocation: formValues.injectionLocation,
        injectionMethod: formValues.injectionMethod,
        certificationMethod: formValues.certificationMethod,
        booster: formValues.booster,
        expirationDate: formValues.expirationDate,
        vaccinator: formValues.vaccinator,
      },
    });

    setIsCreating(false);
    if (result.type === "success") {
      onClose();
      openAlert();
    }
  }, [serviceBus, formValues, organizationId, teamId, healthRecordId, onClose, openAlert]);

  return <AddSameVaccineDialog open={open} onClose={onClose} createVaccine={createVaccine} isCreating={isCreating} />;
};
